// Globe pointer input for the HUD (GDD §14): clicks and hover on the globe. A click is a primary press and release
// that did not move far enough to be a camera orbit; hover is a mouse or pen moving over the globe (touch has no
// hover). Mouse, pen and touch share one path through Pointer Events. This class only recognises the input; the HUD
// decides what it means (a click on an owned city opens its build menu, hover feeds the Intel panel).
//
// Window listeners run in the capture phase so they see globe presses before the camera controls on the canvas;
// they never stop propagation, so orbiting keeps working. The globe is the renderer canvas (GlobePicker.surface): the
// HUD root lets the pointer through to it (pointer-events: none) except over its panels, so an event is on the globe
// exactly when its target is the canvas. Anything else on the page (HUD panels, the tuner's panel beside the game) is
// not the globe.

/** Movement a globe press may have and still count as a click (more is a camera orbit). */
const CLICK_SLOP_PX = 6
const PRIMARY_BUTTON = 0
const TOUCH = 'touch'

type GlobePress = { readonly kind: 'none' } | { readonly kind: 'pressed'; readonly pointerId: number; readonly x: number; readonly y: number }

const NO_GLOBE_PRESS: GlobePress = { kind: 'none' }

function movedBeyond(x0: number, y0: number, x1: number, y1: number, limitPx: number): boolean {
  const dx = x1 - x0
  const dy = y1 - y0
  return dx * dx + dy * dy > limitPx * limitPx
}

export interface GlobePointerHandlers {
  /** A click or tap on the globe (not a camera drag). */
  readonly click: (clientX: number, clientY: number) => void
  /** A mouse or pen over the globe at this point. */
  readonly hover: (clientX: number, clientY: number) => void
  /** The pointer left the globe: over a HUD panel, off the canvas, or out of the window. */
  readonly hoverEnd: () => void
}

export class GlobePointer {
  private press: GlobePress = NO_GLOBE_PRESS

  constructor(
    private readonly surface: Element,
    private readonly handlers: GlobePointerHandlers,
    signal: AbortSignal,
  ) {
    window.addEventListener('pointerdown', this.onPointerDown, { capture: true, signal })
    window.addEventListener('pointerup', this.onPointerUp, { capture: true, signal })
    window.addEventListener('pointercancel', this.onPointerCancel, { capture: true, signal })
    window.addEventListener('pointermove', this.onPointerMove, { capture: true, signal })
    window.addEventListener('pointerout', this.onPointerOut, { capture: true, signal })
  }

  private isOnGlobe(target: EventTarget | null): boolean {
    return target === this.surface
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.button !== PRIMARY_BUTTON || !this.isOnGlobe(event.target)) return
    this.press = { kind: 'pressed', pointerId: event.pointerId, x: event.clientX, y: event.clientY }
  }

  private readonly onPointerUp = (event: PointerEvent): void => {
    const press = this.press
    if (press.kind !== 'pressed' || press.pointerId !== event.pointerId) return
    this.press = NO_GLOBE_PRESS
    if (movedBeyond(press.x, press.y, event.clientX, event.clientY, CLICK_SLOP_PX)) return
    this.handlers.click(event.clientX, event.clientY)
  }

  private readonly onPointerCancel = (event: PointerEvent): void => {
    const press = this.press
    if (press.kind === 'pressed' && press.pointerId === event.pointerId) this.press = NO_GLOBE_PRESS
  }

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (event.pointerType === TOUCH) return
    if (this.isOnGlobe(event.target)) this.handlers.hover(event.clientX, event.clientY)
    else this.handlers.hoverEnd()
  }

  /** A pointerout with no element to go to has left the window. */
  private readonly onPointerOut = (event: PointerEvent): void => {
    if (event.pointerType !== TOUCH && event.relatedTarget === null) this.handlers.hoverEnd()
  }
}
